import axios from 'axios';
import { getAuth } from '../providers/AppAuthProvider';
import { API_BASE_URL as baseUrl } from 'src/utils/api-url';

const resource = 'xbit-relay/transfers';

async function authHeaders() {
  const { accessToken } = (await getAuth()) || {};
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
  };
}

export type TransferStatus =
  | 'Pending'
  | 'PendingAdminApproval'
  | 'Broadcasting'
  | 'Submitting'
  | 'Confirming'
  | 'Completed'
  | 'Failed'
  | 'Rejected';

export type TransferProvider = 'PERPETUAL' | 'INTERNAL_PERPETUAL' | 'TAP_TRADING';

// Mirrors wallet ProviderTransferItem (signature/otp/oidc are NEVER present).
// fromProvider/chainId/token/toAddress/amount are REQUIRED to rebuild the retry/cancel
// signed strings client-side — never hardcode them.
export type TransferItem = {
  id: string;
  userId: string;
  fromProvider: TransferProvider | string;
  toAddress: string;
  chainId: string | number;
  token: string;
  tokenSymbol?: string;
  tokenDecimals?: number;
  amount: string;
  status: TransferStatus | string;
  executeType?: string;
  createdAtUnixMs: string;
  updatedAtUnixMs?: string;
  txHash?: string;
  txId?: string;
  errorMessage?: string;
  errorCode?: string;
  errorMsg?: string;

  fromToken: string
  fromChainId: string
  fromAmount: string
  toAmount: string
  toChainId: string
  toToken: string
};

export type ListTransfersResponse = {
  items: TransferItem[];
  total: string;
  page: number;
  pageSize: number;
};

export type TransferListFilter = {
  page?: number;
  pageSize?: number;
  provider?: TransferProvider | string;
  status?: TransferStatus | string;
  userId?: string;
  recordId?: string;
  toAddress?: string;
  txHash?: string;
  timeFrom?: number;
  timeTo?: number;
};

export async function listTransfers(filter: TransferListFilter = {}) {
  const headers = await authHeaders();
  const params: Record<string, string> = {};
  Object.entries(filter).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    params[k] = String(v);
  });
  return axios.get<ListTransfersResponse>(`${baseUrl}/${resource}`, { headers, params });
}

export type ApproveTransferResponse = { success?: boolean; message?: string };

export async function approveTransfer(
  id: string,
  adminId: string,
  signature: string,
  timestampMs: number,
) {
  const headers = await authHeaders();
  return axios.post<ApproveTransferResponse>(
    `${baseUrl}/${resource}/${id}/approve`,
    { admin_id: adminId, signature, timestamp_ms: timestampMs },
    { headers },
  );
}

export type RejectTransferBody = {
  admin_id: string;
  signature: string;
  timestamp_ms: number;
  note?: string;
};

export type RejectTransferResponse = { success?: boolean; message?: string };

export async function rejectTransfer(id: string, body: RejectTransferBody) {
  const headers = await authHeaders();
  return axios.post<RejectTransferResponse>(`${baseUrl}/${resource}/${id}/reject`, body, {
    headers,
  });
}

export type RetryFailedTransferBody = {
  admin_id: string;
  signature: string;
  timestamp: number;
  evm_nonce?: string;
  gas_limit?: string;
  gas_price?: string;
};

export type RetryFailedTransferResponse = {
  success: boolean;
  message: string;
  txId: string;
};

export async function retryFailedTransfer(id: string, body: RetryFailedTransferBody) {
  const headers = await authHeaders();
  return axios.post<RetryFailedTransferResponse>(
    `${baseUrl}/${resource}/${id}/retry-failed`,
    body,
    { headers },
  );
}

export type CancelFailedTransferBody = {
  admin_id: string;
  signature: string;
  timestamp: number;
  idempotency_key?: string;
  note?: string;
};

export type CancelFailedTransferResponse = {
  success: boolean;
  message: string;
  duplicate: boolean;
};

export async function cancelFailedTransfer(id: string, body: CancelFailedTransferBody) {
  const headers = await authHeaders();
  return axios.post<CancelFailedTransferResponse>(
    `${baseUrl}/${resource}/${id}/cancel-failed`,
    body,
    { headers },
  );
}

export type MarkTransferDoneBody = {
  admin_id: string;
  signature: string;
  timestamp_ms: number;
  note?: string;
};

export type MarkTransferDoneResponse = { success: boolean; message: string };

export async function markTransferDone(id: string, body: MarkTransferDoneBody) {
  const headers = await authHeaders();
  return axios.post<MarkTransferDoneResponse>(
    `${baseUrl}/${resource}/${id}/mark-done`,
    body,
    { headers },
  );
}
